/**
 * Phase 134B — Super Admin Cohort Funnel Dashboard
 * --------------------------------------------------------------------
 * Renders 9 cohort buckets as a cinematic vertical funnel with conversion
 * arrows + drop-off percentages. Plus aggregate metrics (activation rate,
 * cap-to-upgrade rate, time-to-first-stream p50/p90, MRR, top sports).
 *
 * Endpoint: GET /api/livex/admin/cohorts (super_admin only).
 *
 * Strategic intent: visualises where StreamX actually wins or leaks. This is
 * SaaS funnel intelligence, not ERP-style analytics — keep it bold and
 * narrative, not spreadsheet-ish.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import {
  ArrowLeft, AlertTriangle, RefreshCw, BarChart3, Clock, Crown,
  TrendingUp, ChevronDown, Sparkles, Activity, Trophy, Building2,
  MessageCircle, Lightbulb, Flame, ArrowUp, ArrowDown, Quote,
} from 'lucide-react';
import { LivexShimmerKpi, LivexShimmerRow } from '@/components/livex/LivexShimmer';
import '@/styles/livex.css';

// Bucket icon + accent map — keeps the funnel visually narrative.
const BUCKET_META = {
  signed_up:         { icon: Building2,  accent: '#3b82f6' },
  verified:          { icon: Sparkles,   accent: '#60a5fa' },
  activated:         { icon: Activity,   accent: '#FF5A1F' },
  engaged:           { icon: BarChart3,  accent: '#f97316' },
  hit_cap:           { icon: AlertTriangle, accent: '#facc15' },
  upgrade_requested: { icon: TrendingUp, accent: '#a855f7' },
  pro_active:        { icon: Crown,      accent: '#10b981' },
  renewal_pending:   { icon: Clock,      accent: '#eab308' },
  churned:           { icon: Trophy,     accent: '#94A3B8' },
};

// Bar width helper — full funnel max width = signed_up count.
function barPct(value, max) {
  if (!max || max <= 0) return 0;
  return Math.max(4, Math.round((value / max) * 100));
}

function pickWeakLink(buckets) {
  // Identifies the funnel stage with the lowest conversion-from-previous-bucket
  // (>0 prev count, only sequential stages). Returns {label, pct} or null.
  let weakest = null;
  for (const b of buckets) {
    if (b.conversion_from_prev_pct == null) continue;
    if (b.key === 'churned') continue;  // churned isn't a leak, it's an outcome
    if (weakest == null || b.conversion_from_prev_pct < weakest.conversion_from_prev_pct) {
      weakest = b;
    }
  }
  return weakest;
}

function fmtHours(h) {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 48) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} d`;
}

export default function LivexAdminCohorts() {
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState(null);  // Phase 134E
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setRefreshing(true);
    setError('');
    try {
      const [cohortRes, insightRes] = await Promise.all([
        api.get('/livex/admin/cohorts'),
        api.get('/livex/admin/coach/insights?days=7').catch(() => ({ data: null })),
      ]);
      setData(cohortRes.data || null);
      setInsights(insightRes?.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load cohort data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <div className="min-h-screen text-white px-4 sm:px-6 py-6" style={{ background: '#0a0a0f' }} data-testid="livex-admin-cohorts-loading">
        <div className="max-w-5xl mx-auto space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <LivexShimmerKpi key={i} />)}
          </div>
          <div className="space-y-3">
            {Array.from({ length: 9 }).map((_, i) => <LivexShimmerRow key={i} />)}
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen grid place-items-center text-white px-6" style={{ background: '#0a0a0f' }} data-testid="livex-admin-cohorts-error">
        <div className="max-w-md w-full text-center">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-rose-400" />
          <h1 className="text-xl font-black mb-2">Cohort data unavailable</h1>
          <p className="livex-body-text mb-4">{error}</p>
          <button onClick={load} className="inline-flex items-center gap-2 px-5 py-3 text-sm font-bold text-white border-2 border-[#FF5A1F] rounded-lg" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
            <RefreshCw className="h-4 w-4" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const m = data.metrics || {};
  const buckets = data.buckets || [];
  const signedUp = buckets.find((b) => b.key === 'signed_up')?.academies || 0;
  const weakLink = pickWeakLink(buckets);

  return (
    <div className="min-h-screen text-white" style={{ background: '#0a0a0f' }} data-testid="livex-admin-cohorts-page">
      {/* Top bar */}
      <div className="border-b-2 border-[#22314F] bg-[#07111F]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/admin/livex" data-testid="livex-admin-cohorts-back" className="flex-shrink-0 hover:opacity-80 transition-opacity">
              <ArrowLeft className="h-5 w-5 text-white" />
            </Link>
            <div className="h-10 w-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}>
              <BarChart3 className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase">SUPER ADMIN</div>
              <h1 className="text-lg sm:text-xl font-black text-white tracking-tight">Cohort Funnel</h1>
            </div>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            data-testid="livex-admin-cohorts-refresh"
            aria-label="Refresh cohort data"
            className="inline-flex items-center justify-center w-9 h-9 text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-lg transition-all disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="livex-admin-cohorts-kpis">
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <Activity className="h-4 w-4 text-[#FF5A1F] mb-2" />
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums leading-none">{m.activation_rate_pct ?? 0}%</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#94A3B8] mt-1.5">Activation Rate</div>
            <div className="text-[10px] font-bold text-[#94A3B8] mt-0.5">{m.activated_academies}/{m.signed_up_academies} academies</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <TrendingUp className="h-4 w-4 text-emerald-400 mb-2" />
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums leading-none">{m.cap_to_upgrade_rate_pct ?? 0}%</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#94A3B8] mt-1.5">Cap → Upgrade</div>
            <div className="text-[10px] font-bold text-[#94A3B8] mt-0.5">High-intent conversion</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <Clock className="h-4 w-4 text-amber-400 mb-2" />
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums leading-none">{fmtHours(m.time_to_first_stream_hours_p50)}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#94A3B8] mt-1.5">Time to First Stream</div>
            <div className="text-[10px] font-bold text-[#94A3B8] mt-0.5">p50 · p90 {fmtHours(m.time_to_first_stream_hours_p90)}</div>
          </div>
          <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
            <Crown className="h-4 w-4 text-emerald-400 mb-2" />
            <div className="text-2xl sm:text-3xl font-black text-white tabular-nums leading-none">₹{Math.round(m.total_mrr_inr_active || 0).toLocaleString('en-IN')}</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#94A3B8] mt-1.5">Active MRR</div>
            <div className="text-[10px] font-bold text-[#94A3B8] mt-0.5">Pro subscribers · ex-GST</div>
          </div>
        </div>

        {/* Weakest link callout — narrative intelligence */}
        {weakLink && (
          <div
            data-testid="livex-admin-cohorts-weak-link"
            className="p-4 sm:p-5 rounded-2xl border-2 flex items-start gap-3"
            style={{
              background: 'linear-gradient(135deg, #1a0e10, #07111F)',
              borderColor: '#FF5A1F',
              boxShadow: '0 10px 32px rgba(255,90,31,0.18)',
            }}
          >
            <AlertTriangle className="h-5 w-5 text-[#FF5A1F] flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-[#FF5A1F] mb-1">Funnel weak link detected</div>
              <div className="text-base font-black text-white">
                {weakLink.label} converts at <span className="text-[#FF5A1F]">{weakLink.conversion_from_prev_pct}%</span> from {buckets.find((b) => b.key === weakLink.prev_key)?.label || weakLink.prev_key}.
              </div>
              <p className="livex-body-text mt-1" style={{ fontSize: '13px' }}>
                This is where StreamX is leaking. {weakLink.key === 'verified' && 'Improve verification email deliverability or signup UX.'}{weakLink.key === 'activated' && 'Onboarding friction — make first-stream creation a 60-second journey.'}{weakLink.key === 'engaged' && 'Retention drop — academies need a reason to broadcast again.'}{weakLink.key === 'hit_cap' && 'Most academies churn before maxing the pilot — content/season fit issue.'}{weakLink.key === 'upgrade_requested' && 'Pricing or value-proposition friction at the cap moment.'}{weakLink.key === 'pro_active' && 'Manual provisioning lag — Razorpay self-serve unblocks this.'}
              </p>
            </div>
          </div>
        )}

        {/* Vertical funnel */}
        <section data-testid="livex-admin-cohorts-funnel">
          <div className="text-[11px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase mb-2">Funnel</div>
          <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight mb-4">Academy lifecycle stages</h2>
          <div className="space-y-3">
            {buckets.map((b, idx) => {
              const meta = BUCKET_META[b.key] || { icon: Activity, accent: '#94A3B8' };
              const Icon = meta.icon;
              const widthPct = barPct(b.academies, signedUp);
              const showArrow = idx < buckets.length - 1 && buckets[idx + 1]?.prev_key === b.key;
              const nextConv = showArrow ? buckets[idx + 1]?.conversion_from_prev_pct : null;
              return (
                <div key={b.key} className="relative">
                  <div
                    data-testid={`livex-admin-cohorts-bucket-${b.key}`}
                    className="relative p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden livex-card-lift"
                  >
                    {/* Funnel bar — width proportional to academies */}
                    <div
                      aria-hidden
                      className="absolute inset-y-0 left-0 pointer-events-none transition-all"
                      style={{
                        width: `${widthPct}%`,
                        background: `linear-gradient(90deg, ${meta.accent}22 0%, ${meta.accent}05 100%)`,
                      }}
                    />
                    <div className="relative flex items-center gap-4 flex-wrap">
                      <div
                        className="flex-shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl border-2"
                        style={{ borderColor: `${meta.accent}55`, background: `${meta.accent}11` }}
                      >
                        <Icon className="h-5 w-5" style={{ color: meta.accent }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-black text-white">{b.label}</span>
                          {b.conversion_from_prev_pct != null && b.prev_key && (
                            <span
                              className="text-[10px] font-black uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
                              style={{
                                background: b.conversion_from_prev_pct >= 50 ? '#052e2a' :
                                            b.conversion_from_prev_pct >= 25 ? '#1a200a' : '#2a0a0e',
                                color: b.conversion_from_prev_pct >= 50 ? '#6ee7b7' :
                                       b.conversion_from_prev_pct >= 25 ? '#facc15' : '#fda4af',
                              }}
                            >
                              {b.conversion_from_prev_pct}% from {buckets.find((x) => x.key === b.prev_key)?.label?.split(' ')[0] || b.prev_key}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-2xl font-black text-white tabular-nums leading-none" style={{ color: meta.accent }}>{b.academies}</div>
                        <div className="text-[10px] font-bold text-[#94A3B8] uppercase tracking-[0.1em]">academies</div>
                      </div>
                    </div>
                  </div>
                  {showArrow && (
                    <div className="flex justify-center my-1">
                      <div
                        data-testid={`livex-admin-cohorts-arrow-${b.key}`}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-[#050a14] border border-[#22314F] rounded-full text-[10px] font-bold tabular-nums"
                        style={{
                          color: nextConv >= 50 ? '#6ee7b7' :
                                 nextConv >= 25 ? '#facc15' : '#fda4af',
                        }}
                      >
                        <ChevronDown className="h-3 w-3" />
                        {nextConv != null ? `${nextConv}%` : '—'}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Phase 134C — Time-to-First-Stream histogram. The single biggest
            churn vector as StreamX scales: signup → first stream. Surfaces
            both speed (under_1h etc.) and the "never" bucket which represents
            the activation gap. */}
        {(data.ttfs_histogram || []).length > 0 && (
          <section data-testid="livex-admin-cohorts-ttfs">
            <div className="text-[11px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase mb-2">Activation Speed</div>
            <h2 className="text-xl font-black text-white tracking-tight mb-3">Time to first stream</h2>
            {(() => {
              const max = Math.max(1, ...data.ttfs_histogram.map((b) => b.academies || 0));
              return (
                <div className="space-y-2">
                  {data.ttfs_histogram.map((b) => {
                    const w = Math.max(2, Math.round(((b.academies || 0) / max) * 100));
                    // Color gradient — fast = green, slow = amber, never = rose.
                    const accent = b.key === 'under_1h'  ? '#10b981' :
                                   b.key === 'under_24h' ? '#22c55e' :
                                   b.key === 'under_48h' ? '#facc15' :
                                   b.key === 'over_48h'  ? '#f97316' :
                                                            '#dc2626';
                    return (
                      <div
                        key={b.key}
                        data-testid={`livex-admin-cohorts-ttfs-${b.key}`}
                        className="relative p-3 bg-[#07111F] border-2 border-[#22314F] rounded-xl overflow-hidden"
                      >
                        <div
                          aria-hidden
                          className="absolute inset-y-0 left-0 pointer-events-none transition-all"
                          style={{
                            width: `${w}%`,
                            background: `linear-gradient(90deg, ${accent}33 0%, ${accent}08 100%)`,
                          }}
                        />
                        <div className="relative flex items-center justify-between gap-3">
                          <div className="text-sm font-black text-white">{b.label}</div>
                          <div className="text-xl font-black tabular-nums leading-none" style={{ color: accent }}>{b.academies}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <p className="text-[11px] font-bold text-[#94A3B8] mt-2">
              <span className="text-rose-300">"Never activated"</span> is your biggest churn vector — these academies signed up but haven't broadcast yet. Activation nudges fire daily at 10:00 UTC.
            </p>
          </section>
        )}

        {/* Phase 134E — Coach Insights. Product intelligence mined from
            livex_concierge_messages. Surfaces top questions, category
            distribution, anonymized excerpts, heuristic suggestions. */}
        {insights && insights.totals?.user_questions > 0 && (
          <section data-testid="livex-admin-cohorts-coach-insights" className="space-y-4">
            <div>
              <div className="text-[11px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase mb-2 flex items-center gap-2">
                <MessageCircle className="h-3.5 w-3.5" /> Coach Insights · last {insights.window_days || 7} days
              </div>
              <h2 className="text-xl sm:text-2xl font-black text-white tracking-tight">What academies are actually asking</h2>
              <div className="flex items-center gap-3 mt-1 text-[12px] font-bold text-[#94A3B8] flex-wrap">
                <span><span className="text-white font-black tabular-nums">{insights.totals.user_questions}</span> questions</span>
                <span>·</span>
                <span><span className="text-white font-black tabular-nums">{insights.totals.unique_question_patterns}</span> unique patterns</span>
                <span>·</span>
                <span><span className="text-white font-black tabular-nums">{insights.totals.distinct_academies_asking}</span> academies asking</span>
                {insights.trend?.previous_window_count > 0 && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-black"
                    style={{
                      color: insights.trend.delta_pct >= 0 ? '#6ee7b7' : '#fda4af',
                      background: insights.trend.delta_pct >= 0 ? '#052e2a' : '#2a0a0e',
                    }}>
                    {insights.trend.delta_pct >= 0 ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                    {Math.abs(insights.trend.delta_pct)}% vs prior
                  </span>
                )}
              </div>
            </div>

            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4">
              {/* Top questions panel */}
              <div className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-4 sm:p-5" data-testid="livex-admin-cohorts-top-questions">
                <div className="flex items-center gap-2 mb-3">
                  <Flame className="h-4 w-4 text-[#FF5A1F]" />
                  <h3 className="text-sm font-black text-white tracking-tight">Top questions</h3>
                </div>
                {(insights.top_questions || []).length === 0 ? (
                  <p className="livex-body-text" style={{ fontSize: '13px' }}>No questions in this window.</p>
                ) : (
                  <ol className="space-y-2">
                    {insights.top_questions.map((q) => {
                      const meta = (insights.categories || []).find((c) => c.key === q.category);
                      return (
                        <li
                          key={q.rank}
                          data-testid={`livex-admin-cohorts-top-question-${q.rank}`}
                          className="flex items-start gap-3 p-3 bg-[#050a14] border border-[#22314F] rounded-xl"
                        >
                          <div
                            className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-lg text-[12px] font-black"
                            style={{
                              background: `${meta?.color || '#FF5A1F'}22`,
                              color: meta?.color || '#FF5A1F',
                              border: `1.5px solid ${(meta?.color || '#FF5A1F')}55`,
                            }}
                          >
                            #{q.rank}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-white truncate">{q.example}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span
                                className="text-[10px] font-black uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
                                style={{ color: meta?.color || '#94A3B8', background: '#0c1a2e' }}
                              >
                                {meta?.label || q.category}
                              </span>
                              <span className="text-[11px] font-bold text-[#94A3B8] tabular-nums">{q.count} mention{q.count === 1 ? '' : 's'}</span>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>

              {/* Categories panel */}
              <div className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-4 sm:p-5" data-testid="livex-admin-cohorts-categories">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="h-4 w-4 text-[#FF5A1F]" />
                  <h3 className="text-sm font-black text-white tracking-tight">Confusion categories</h3>
                </div>
                <div className="space-y-2">
                  {(insights.categories || []).filter((c) => c.count > 0).map((c) => (
                    <div
                      key={c.key}
                      data-testid={`livex-admin-cohorts-category-${c.key}`}
                      className="relative p-2.5 bg-[#050a14] border border-[#22314F] rounded-lg overflow-hidden"
                    >
                      <div
                        aria-hidden
                        className="absolute inset-y-0 left-0 pointer-events-none"
                        style={{
                          width: `${Math.max(2, c.pct)}%`,
                          background: `linear-gradient(90deg, ${c.color}33 0%, ${c.color}05 100%)`,
                        }}
                      />
                      <div className="relative flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
                          <span className="text-sm font-bold text-white">{c.label}</span>
                        </div>
                        <div className="text-[12px] font-bold text-[#94A3B8] tabular-nums">
                          <span className="text-white font-black">{c.count}</span> · {c.pct}%
                        </div>
                      </div>
                    </div>
                  ))}
                  {(insights.categories || []).filter((c) => c.count > 0).length === 0 && (
                    <p className="livex-body-text" style={{ fontSize: '13px' }}>No categorised questions yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Anonymized excerpts */}
            {(insights.excerpts || []).length > 0 && (
              <div className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl p-4 sm:p-5" data-testid="livex-admin-cohorts-excerpts">
                <div className="flex items-center gap-2 mb-3">
                  <Quote className="h-4 w-4 text-[#FF5A1F]" />
                  <h3 className="text-sm font-black text-white tracking-tight">Voices from real academies</h3>
                  <span className="text-[11px] font-bold text-[#94A3B8]">anonymized</span>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {insights.excerpts.map((ex, i) => {
                    const catMeta = (insights.categories || []).find((c) => c.key === ex.category);
                    return (
                      <div
                        key={i}
                        data-testid={`livex-admin-cohorts-excerpt-${i}`}
                        className="p-3 bg-[#050a14] border border-[#22314F] rounded-lg"
                      >
                        <div className="text-sm font-medium text-white leading-snug">"{ex.text}"</div>
                        <div className="mt-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] rounded"
                          style={{ color: catMeta?.color || '#94A3B8', background: '#0c1a2e' }}>
                          {catMeta?.label || ex.category}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Heuristic suggestions */}
            {(insights.suggestions || []).length > 0 && (
              <div
                data-testid="livex-admin-cohorts-suggestions"
                className="rounded-2xl border-2 p-4 sm:p-5"
                style={{
                  background: 'linear-gradient(135deg, #1a0e10, #07111F)',
                  borderColor: '#FF5A1F',
                  boxShadow: '0 10px 32px rgba(255,90,31,0.20)',
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Lightbulb className="h-4 w-4 text-[#FF5A1F]" />
                  <h3 className="text-sm font-black text-white tracking-tight">Suggested product improvements</h3>
                  <span className="text-[10px] font-bold text-[#fed7aa] uppercase tracking-[0.12em]">heuristic · auto-generated</span>
                </div>
                <ul className="space-y-2.5">
                  {insights.suggestions.map((s, i) => (
                    <li
                      key={i}
                      data-testid={`livex-admin-cohorts-suggestion-${i}`}
                      className="flex items-start gap-3 p-3 bg-[#050a14] border border-[#22314F] rounded-xl"
                    >
                      <Sparkles className="h-4 w-4 text-[#FF5A1F] flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-black text-white">{s.headline}</span>
                          <span
                            className="text-[10px] font-black uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
                            style={{
                              background: s.impact === 'high' ? '#2a0a0e' : '#0c1a2e',
                              color: s.impact === 'high' ? '#fda4af' : '#fed7aa',
                            }}
                          >
                            impact: {s.impact}
                          </span>
                        </div>
                        <p className="livex-body-text mt-1" style={{ fontSize: '13px' }}>{s.rationale}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Top sports — secondary intelligence */}
        {(data.top_sports || []).length > 0 && (
          <section data-testid="livex-admin-cohorts-sports">
            <div className="text-[11px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase mb-2">By sport</div>
            <h2 className="text-xl font-black text-white tracking-tight mb-3">Streaming distribution</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {data.top_sports.map((s) => (
                <div key={s.sport} className="p-3 bg-[#07111F] border-2 border-[#22314F] rounded-xl">
                  <div className="text-2xl font-black text-white tabular-nums leading-none">{s.count}</div>
                  <div className="text-xs font-bold text-[#94A3B8] uppercase mt-1 truncate" title={s.sport}>{s.sport}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="text-[10px] font-bold text-[#94A3B8] text-center pt-2">
          Snapshot generated {new Date(data.generated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
        </div>
      </div>
    </div>
  );
}
